import { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'click', 'scroll', 'touchstart', 'wheel'];

/**
 * Logs the user out after `timeoutMs` with no mouse/keyboard/scroll activity.
 * Activity is tracked as a timestamp so a laptop that sleeps (timers paused)
 * is still logged out on wake: the check also runs on visibilitychange/focus.
 */
const useIdleLogout = (timeoutMs = 15 * 60 * 1000, enabled = true) => {
  const history = useHistory();
  const lastActivityRef = useRef(Date.now());
  const loggedOutRef = useRef(false);

  useEffect(() => {
    if (!enabled) return undefined;

    lastActivityRef.current = Date.now();
    loggedOutRef.current = false;
    let timer = null;

    const logoutNow = () => {
      if (loggedOutRef.current) return;
      loggedOutRef.current = true;
      history.push('/logout');
    };

    const schedule = () => {
      clearTimeout(timer);
      const remaining = timeoutMs - (Date.now() - lastActivityRef.current);
      if (remaining <= 0) {
        logoutNow();
        return;
      }
      timer = setTimeout(schedule, remaining);
    };

    // throttle resets — mousemove fires constantly
    let lastReset = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - lastReset < 1000) return;
      lastReset = now;
      lastActivityRef.current = now;
      schedule();
    };

    // returning to the tab: check first (may already be past the limit), then count it as activity
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastActivityRef.current >= timeoutMs) {
        logoutNow();
        return;
      }
      onActivity();
    };

    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    schedule();

    return () => {
      clearTimeout(timer);
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [timeoutMs, enabled, history]);
};

export default useIdleLogout;
